import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Clock, User, Stethoscope, Loader2, Calendar as CalendarIcon,
  AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QueuePage() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/queue`, {
        params: { date: format(selectedDate, 'yyyy-MM-dd') },
        withCredentials: true
      });
      setQueue(data.queue || []);
      setDoctors(data.doctors || []);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  }, [selectedDate]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const handleAssign = async (cardId, doctorId) => {
    setAssigning(cardId);
    try {
      const { data } = await axios.put(`${API_URL}/api/queue/${cardId}/assign`, { doctor_id: doctorId }, { withCredentials: true });
      toast.success(`Assigned to ${data.doctor_name}`);
      fetchQueue();
    } catch { toast.error('Failed to assign'); }
    finally { setAssigning(null); }
  };

  const waitColor = (mins) => {
    if (mins > 60) return 'text-red-600 bg-red-50';
    if (mins > 30) return 'text-amber-600 bg-amber-50';
    return 'text-emerald-600 bg-emerald-50';
  };

  return (
    <div className="space-y-4" data-testid="queue-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-heading font-bold text-slate-800">Patient Queue</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Manage wait times and doctor assignments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setSelectedDate(d => { const p = new Date(d); p.setDate(p.getDate()-1); return p; })} className="h-8 w-8">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-8 gap-2 text-xs rounded-full">
                <CalendarIcon className="w-3.5 h-3.5" />
                {format(selectedDate, 'dd MMM yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={selectedDate} onSelect={d => { if(d){setSelectedDate(d);setCalendarOpen(false);} }} /></PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" onClick={() => setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate()+1); return n; })} className="h-8 w-8">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={fetchQueue} className="h-8 gap-1 text-xs rounded-full">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-[#0E5132]">{queue.length}</p>
            <p className="text-[10px] text-emerald-600 uppercase tracking-wider">In Queue</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{queue.filter(q => !q.assigned_doctor_id).length}</p>
            <p className="text-[10px] text-amber-600 uppercase tracking-wider">Unassigned</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-red-600">{queue.filter(q => q.wait_minutes > 30).length}</p>
            <p className="text-[10px] text-red-500 uppercase tracking-wider">&gt;30min Wait</p>
          </CardContent>
        </Card>
        <Card className="border-sky-200 bg-sky-50/50">
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-sky-700">{queue.length > 0 ? Math.round(queue.reduce((s,q) => s+q.wait_minutes, 0)/queue.length) : 0}m</p>
            <p className="text-[10px] text-sky-500 uppercase tracking-wider">Avg Wait</p>
          </CardContent>
        </Card>
      </div>

      {/* Queue list */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0E5132]" /></div>
      ) : queue.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-10 text-center">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No patients in queue for this date</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {queue.map((item) => (
            <Card key={item.card_id} className="border-slate-200 hover:border-emerald-300 transition-colors cursor-pointer" data-testid={`queue-item-${item.card_id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0" onClick={() => navigate(`/consult-cards/${item.card_id}`)}>
                    <div className="w-8 h-8 rounded-full bg-[#E6EFEA] flex items-center justify-center text-sm font-bold text-[#0E5132] shrink-0">
                      {item.position}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm truncate">{item.patient_name}</p>
                        {item.needs_attention && (
                          <Badge className="text-[8px] bg-amber-100 text-amber-700 border-amber-200">
                            <AlertTriangle className="w-2 h-2 mr-0.5" /> Needs Attention
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{item.patient_phone} {item.eden_service_name && `· ${item.eden_service_name}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 ml-11 sm:ml-0">
                    <Badge className={`text-xs ${waitColor(item.wait_minutes)} border-0 font-mono shrink-0`}>
                      <Clock className="w-3 h-3 mr-1" />
                      {item.wait_minutes < 60 ? `${item.wait_minutes}m` : `${Math.floor(item.wait_minutes/60)}h ${item.wait_minutes%60}m`}
                    </Badge>
                    <div className="w-36 sm:w-44">
                      <Select
                        value={item.assigned_doctor_id || ""}
                        onValueChange={(val) => handleAssign(item.card_id, val)}
                        disabled={assigning === item.card_id}
                      >
                        <SelectTrigger className="h-8 text-xs rounded-full border-violet-200" data-testid={`assign-doctor-${item.card_id}`}>
                          <Stethoscope className="w-3 h-3 mr-1 text-violet-500" />
                          <SelectValue placeholder="Assign Doctor" />
                        </SelectTrigger>
                        <SelectContent>
                          {doctors.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
